/**
 * API service module
 * Handles all HTTP requests to the backend
 */
import axios from 'axios'
import { config as appConfig } from '@/config'
import serviceDiscovery, { getPortalApiUrl } from './discovery'

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: getPortalApiUrl(), // Use dynamic service discovery
  timeout: appConfig.api.timeout,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(appConfig.auth.tokenKey)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      localStorage.removeItem(appConfig.auth.tokenKey)
      window.location.href = appConfig.auth.loginRedirect
    }
    return Promise.reject(error)
  }
)

// Create a separate axios instance for auth service
// When accessed through Cloudflare tunnel, use the same domain
const isTunnel = window.location.hostname === 'admin.aushadhhealth.com'
const authBaseURL = isTunnel 
  ? '' // Use relative URL when through tunnel (portal API will proxy)
  : (process.env.VITE_AUTH_SERVICE_URL || appConfig.auth.serviceUrl || 'http://localhost:8002')

const authClient = axios.create({
  baseURL: authBaseURL,
  timeout: appConfig.api.timeout,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Auth API - using auth service directly
export const authApi = {
  login: async (credentials) => {
    // Use the auth client with proper configuration
    const response = await authClient.post('/api/v1/auth/login', credentials)
    return response
  },
  logout: () => apiClient.post('/auth/logout'),
  validateToken: () => apiClient.get('/auth/validate'),
  setAuthToken: (token) => {
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete apiClient.defaults.headers.common['Authorization']
    }
  }
}

// Admin API
export const adminApi = {
  // Dashboard
  getDashboard: () => apiClient.get('/admin/dashboard'),
  
  // Beta Users
  getBetaUsers: (params) => apiClient.get('/admin/beta-users', { params }),
  approveBetaUser: (userId) => apiClient.post(`/admin/beta-users/${userId}/approve`),
  rejectBetaUser: (userId) => apiClient.delete(`/admin/beta-users/${userId}`),
  exportBetaUsers: (format) => apiClient.get('/admin/beta-users/export', { 
    params: { format },
    responseType: 'blob' 
  }),
  
  // Feature Flags
  getFeatureFlags: () => apiClient.get('/admin/feature-flags'),
  createFeatureFlag: (data) => apiClient.post('/admin/feature-flags', data),
  updateFeatureFlag: (flagId, data) => apiClient.put(`/admin/feature-flags/${flagId}`, data),
  deleteFeatureFlag: (flagId) => apiClient.delete(`/admin/feature-flags/${flagId}`),
  checkFeatureFlag: (flag, userId) => apiClient.get('/admin/feature-flags/check', {
    params: { flag, user_id: userId }
  }),
  
  // User Feedback
  getFeedback: (params) => apiClient.get('/admin/feedback', { params }),
  assignFeedback: (feedbackId, assignedTo) => apiClient.post(`/admin/feedback/${feedbackId}/assign`, {
    assigned_to: assignedTo
  }),
  resolveFeedback: (feedbackId, resolution) => apiClient.post(`/admin/feedback/${feedbackId}/resolve`, {
    resolution
  }),
  
  // News Management
  getNews: (params) => apiClient.get('/admin/news', { params }),
  createNews: (data) => apiClient.post('/admin/news', data),
  updateNews: (articleId, data) => apiClient.put(`/admin/news/${articleId}`, data),
  deleteNews: (articleId) => apiClient.delete(`/admin/news/${articleId}`),
  
  // AI Prompts
  getAIPrompts: () => apiClient.get('/admin/ai-prompts'),
  createAIPrompt: (data) => apiClient.post('/admin/ai-prompts', data),
  activateAIPrompt: (promptId) => apiClient.post(`/admin/ai-prompts/${promptId}/activate`),
  
  // Audit Logs
  getAuditLogs: (params) => apiClient.get('/admin/audit-logs', { params }),
  exportAuditLogs: (params) => apiClient.get('/admin/audit-logs/export', {
    params,
    responseType: 'blob'
  }),
  
  // Email Management
  getEmails: (params) => apiClient.get('/admin/emails', { params }),
  markEmailRead: (emailId) => apiClient.put(`/admin/emails/${emailId}/read`),
  toggleEmailStar: (emailId, starred) => apiClient.put(`/admin/emails/${emailId}/star`, { starred }),
  archiveEmail: (emailId) => apiClient.put(`/admin/emails/${emailId}/archive`),
  deleteEmail: (emailId) => apiClient.delete(`/admin/emails/${emailId}`)
}

// Health API
export const healthApi = {
  getServiceHealth: () => apiClient.get('/admin/services/health'),
  getServiceDetails: (serviceName) => apiClient.get(`/admin/services/health/${serviceName}`)
}

// Public API
export const publicApi = {
  getNews: (params) => apiClient.get('/news', { params }),
  getNewsArticle: (articleId) => apiClient.get(`/news/${articleId}`),
  subscribeNewsletter: (data) => apiClient.post('/newsletter/subscribe', data),
  unsubscribeNewsletter: (token) => apiClient.post(`/newsletter/unsubscribe/${token}`),
  getStats: () => apiClient.get('/stats'),
  submitContact: (data) => apiClient.post('/contact', data)
}

export default apiClient